/**
 * The width of the orcaloops frame's right panel (properties), which a person
 * resizes by dragging its edge.
 *
 * The width starts at {@link RIGHT_WIDTH_DEFAULT}, is always held inside
 * {@link RIGHT_WIDTH_MIN}–{@link RIGHT_WIDTH_MAX}, and is kept in the browser
 * under {@link ORCALOOPS_RIGHT_WIDTH_KEY} the same way the theme is.
 * {@link OrcaloopsFrame} writes it as `--ol-right-w` on `.ol-body`, which is
 * what `orcaloops.css` (and the run page's `v3.css`) size the right track from.
 *
 * SSR/jsdom-safe: every `window` / `localStorage` access is guarded, and the
 * pure {@link computeInitialRightWidth} takes the window as an argument so the
 * decision is unit-tested without a browser. Mirrors `orcaloops-left-width.ts`.
 *
 * No `useState`, no `useEffect`.
 *
 * @since 1.0.0
 */
import { Atom } from "@effect-atom/atom-react";

import type { ThemeWindowLike } from "./clawinstall-theme.ts";

/** The browser-storage key a chosen width is kept under. @since 1.0.0 */
export const ORCALOOPS_RIGHT_WIDTH_KEY = "orcaloops-right-width";

/** The width the panel starts at, and returns to on a double-click. @since 1.0.0 */
export const RIGHT_WIDTH_DEFAULT = 320;

/** The narrowest the panel goes. @since 1.0.0 */
export const RIGHT_WIDTH_MIN = 260;

/** The widest the panel goes. @since 1.0.0 */
export const RIGHT_WIDTH_MAX = 720;

/** A width held inside {@link RIGHT_WIDTH_MIN}–{@link RIGHT_WIDTH_MAX}, to the whole pixel. @since 1.0.0 */
export const clampRightWidth = (width: number): number =>
  Math.min(RIGHT_WIDTH_MAX, Math.max(RIGHT_WIDTH_MIN, Math.round(width)));

/**
 * The width to start at: the stored width, clamped, when it is an integer;
 * else {@link RIGHT_WIDTH_DEFAULT}. `null` (no window: the server) is the
 * default.
 *
 * @since 1.0.0
 */
export const computeInitialRightWidth = (win: ThemeWindowLike | null): number => {
  const stored = win?.localStorage?.getItem(ORCALOOPS_RIGHT_WIDTH_KEY);
  if (stored === undefined || stored === null || !/^\d+$/.test(stored)) return RIGHT_WIDTH_DEFAULT;
  return clampRightWidth(Number(stored));
};

/**
 * {@link computeInitialRightWidth} over the real window, or the default on the
 * server. Read after mount, never at import.
 *
 * @since 1.0.0
 */
export const getInitialOrcaloopsRightWidth = (): number => {
  if (typeof window === "undefined") return computeInitialRightWidth(null);
  return computeInitialRightWidth(window as ThemeWindowLike);
};

/** Keep a chosen width so the next visit starts at it. A no-op on the server. @since 1.0.0 */
export const persistOrcaloopsRightWidth = (width: number): void => {
  if (typeof window === "undefined") return;
  (window as ThemeWindowLike).localStorage?.setItem(ORCALOOPS_RIGHT_WIDTH_KEY, String(width));
};

/**
 * The live width. Kept alive so a resize survives every route change on the
 * member surface. It starts at {@link RIGHT_WIDTH_DEFAULT} on both sides, so the
 * client's first frame matches the server's; {@link OrcaloopsFrame} applies the
 * stored width ({@link getInitialOrcaloopsRightWidth}) after mount.
 *
 * @since 1.0.0
 */
export const orcaloopsRightWidthAtom: Atom.Writable<number> = Atom.make<number>(RIGHT_WIDTH_DEFAULT).pipe(
  Atom.keepAlive,
);
